import { useState } from 'react';
import { Dialog, DialogActions } from '../design-system/Overlay';
import type { Deviation } from './form-targets';

/**
 * Question de non-conformité à la validation (ADR-141 §1.3) : les écarts constatés, champ, valeur et
 * cible ; « Corriger la saisie » revient au formulaire, « Valider sans NC » n'est pas offert quand un
 * écart exige la non-conformité (`nc_required`), « Ouvrir une NC » envoie les chemins cochés dans
 * `result.nonconformities`. Le serveur ne crée pas la non-conformité : il revérifie, et refuse une
 * non-conformité obligatoire absente.
 */
export function NonconformityDialog({ deviations, onCorrect, onConfirm }: { deviations: readonly Deviation[]; onCorrect: () => void; onConfirm: (nonconformities: string[]) => void }) {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set(deviations.map((entry) => entry.path)));
  const required = deviations.some((entry) => entry.policy === 'nc_required');
  const count = deviations.length;
  const title = `${count} ${count > 1 ? 'écarts constatés' : 'écart constaté'} — Ouvrir une non-conformité ?`;
  const toggle = (path: string, on: boolean): void =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(path);
      else next.delete(path);
      return next;
    });

  return (
    <Dialog
      title={title}
      open
      onClose={onCorrect}
      size="md"
      note={required ? 'Un écart au moins exige la non-conformité.' : undefined}
      footer={
        <DialogActions
          secondary={{ label: 'Valider sans NC', disabled: required, onClick: () => onConfirm([]) }}
          cancel={{ label: 'Corriger la saisie', onClick: onCorrect }}
          primary={{ label: 'Ouvrir une NC', disabled: chosen.size === 0, onClick: () => onConfirm(deviations.map((entry) => entry.path).filter((path) => chosen.has(path) || deviations.some((entry) => entry.path === path && entry.policy === 'nc_required'))) }}
        />
      }
    >
      <ul className="ay-deviations" aria-label="Écarts constatés">
        {deviations.map((entry) => (
          <li key={entry.path}>
            <input
              type="checkbox"
              aria-label={`Déclarer « ${entry.label} »`}
              checked={entry.policy === 'nc_required' || chosen.has(entry.path)}
              disabled={entry.policy === 'nc_required'}
              onChange={(event) => toggle(entry.path, event.target.checked)}
            />
            <span className="ay-deviations__label">{entry.label}</span>
            <span className="ay-deviations__value">{entry.value}</span>
            <span className="ay-deviations__target">{entry.target}</span>
            {entry.policy === 'nc_required' && <span className="ay-badge ay-badge--danger">NC obligatoire</span>}
          </li>
        ))}
      </ul>
      <p className="ay-text">La non-conformité reprend la tâche et les réponses hors cible ; son traitement suit côté qualité. Le serveur revérifie les cibles à la validation.</p>
    </Dialog>
  );
}
